import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import MobileToolbar from 'components/Layout/MobileShell/MobileToolbar';
import MobileFilterSheet from 'components/Layout/MobileShell/MobileFilterSheet';
import MobileListState from 'components/Layout/MobileShell/MobileListState';
import { useGetUsinasEolicasQuery } from 'services/api/windx';
import { ConjuntoAgregado, OrdenacaoConjuntos, UsinaEolicaDenormalizada } from 'helper/windx';
import agregarConjuntos from 'helper/agregarConjuntos';
import filtrarConjuntos from 'helper/filtrarConjuntos';
import ordenarConjuntos from 'helper/ordenarConjuntos';
import useFiltrosConjuntos from 'hooks/Windx/useFiltrosConjuntos';
import useSelecaoConjuntos from 'hooks/Windx/useSelecaoConjuntos';
import {
  selectWindxIdsEntidades,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import FiltroCategoria from '../FiltroCategoria';
import LinhaSelecao from '../LinhaSelecao';
import Rodape from '../Rodape';

// Mobile nao tem cabecalho de coluna clicavel — a ordenacao fica fixa no
// codigo do conjunto, o mesmo default do desktop.
const ORDENACAO_FIXA: OrdenacaoConjuntos = { coluna: 'cod_conjunto', direcao: 'asc' };

type Categoria = 'uf' | 'sm' | 'sh' | 'epe' | '';

function extrairUnicos(
  itens: ConjuntoAgregado[],
  campo: (c: ConjuntoAgregado) => string | null,
): string[] {
  const set = new Set<string>();
  itens.forEach((c) => {
    const v = campo(c);
    if (v !== null && v !== undefined && v !== '') set.add(v);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

function formatarMw(valor: number): string {
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MW`;
}

/**
 * Seletor de conjuntos (subdivisao=3) no mobile.
 *
 * Reusa do desktop tudo que nao e markup: o fetch unico de usinas (mesmo cache
 * RTK Query do seletor de usinas), `agregarConjuntos`, `filtrarConjuntos`,
 * `ordenarConjuntos`, `useFiltrosConjuntos` e `useSelecaoConjuntos`. So a
 * apresentacao muda — tabela virtualizada vira lista, dropdowns viram
 * `MobileFilterSheet`.
 */
export default function Conjuntos({
  open, dismiss,
}: { open: boolean; dismiss: () => void }) {
  const dispatch = useDispatch();
  const idsSelecionadosRedux = useSelector(selectWindxIdsEntidades);

  const usinasQuery = useGetUsinasEolicasQuery(undefined, { skip: !open });
  const usinas = useMemo<UsinaEolicaDenormalizada[]>(
    () => usinasQuery.data?.data ?? [],
    [usinasQuery.data],
  );
  const conjuntos = useMemo(() => agregarConjuntos(usinas), [usinas]);

  const filtrosHook = useFiltrosConjuntos();
  const selecao = useSelecaoConjuntos({ inicial: idsSelecionadosRedux });
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [categoriaAberta, setCategoriaAberta] = useState<Categoria>('');

  // Sincroniza buffer local com o Redux quando o sheet abre.
  useEffect(() => {
    if (open) {
      selecao.resetar(idsSelecionadosRedux);
      filtrosHook.limparFiltros();
    }
    // Intencional: omitir `selecao`/`filtrosHook` para nao resetar a cada
    // toggle interno (mesma razao do desktop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ufsDisponiveis = useMemo(() => extrairUnicos(conjuntos, (c) => c.uf_sigla), [conjuntos]);
  const smDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.subsistema_sm_sigla),
    [conjuntos],
  );
  const shDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.subsistema_sh_sigla),
    [conjuntos],
  );
  const epeDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.localizacao_epe),
    [conjuntos],
  );

  const filtrados = useMemo(
    () => ordenarConjuntos(
      filtrarConjuntos(conjuntos, filtrosHook.filtrosEfetivos),
      ORDENACAO_FIXA,
    ),
    [conjuntos, filtrosHook.filtrosEfetivos],
  );

  const conjuntosPorId = useMemo(() => {
    const m = new Map<number, ConjuntoAgregado>();
    conjuntos.forEach((c) => m.set(c.id, c));
    return m;
  }, [conjuntos]);

  const { filtros } = filtrosHook;
  const chips = [
    ...filtros.ufs,
    ...filtros.subsistemasSm,
    ...filtros.subsistemasSh,
    ...filtros.localizacoesEpe,
  ].map((v) => <span key={v}>{v}</span>);

  const toggleCategoria = (cat: Categoria) => () => (
    setCategoriaAberta((atual) => (atual === cat ? '' : cat))
  );

  const handleAplicar = () => {
    // 2026-05-29: aplicar a selecao NAO dispara realizarConsulta — o usuario
    // decide quando recarregar, para ler o aviso de complexidade antes.
    dispatch(setIdsEntidades(selecao.ids));
    dismiss();
  };

  const agregado = selecao.totalAgregado(conjuntosPorId);
  const resumo = selecao.count === 0
    ? 'Nenhum conjunto selecionado'
    : `${selecao.count} selecionado${selecao.count === 1 ? '' : 's'} · ${agregado.totalUsinas} usina${agregado.totalUsinas === 1 ? '' : 's'} · ${formatarMw(agregado.totalPotenciaMw)}`;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Selecionar conjuntos"
      className="windx-seletor-sheet windx-seletor-sheet--grande"
    >
      <>
        {usinasQuery.error && (
        <Alert variant="danger" className="m-0">
          Não foi possível carregar os conjuntos. Tente novamente em instantes.
        </Alert>
        )}

        {!usinasQuery.error && (
        <>
          <MobileToolbar
            searchValue={filtros.busca}
            onSearchChange={(e) => filtrosHook.setBusca(e.target.value)}
            setSearchQuery={filtrosHook.setBusca}
            searchId="windx-busca-conjuntos"
            searchPlaceholder="Buscar conjunto"
            filterActive={chips.length > 0}
            filterAriaLabel="Filtrar conjuntos"
            appliedFilters={chips}
            onFilterClick={() => setFiltrosAbertos(true)}
            onClearAll={filtrosHook.limparFiltros}
            resultsCount={filtrados.length}
            resultsNoun="conjunto"
            loading={usinasQuery.isLoading}
          />

          <div className="windx-seletor-sheet-lista">
            <MobileListState
              loading={usinasQuery.isLoading}
              isEmpty={filtrados.length === 0}
              emptyMessage="Nenhum conjunto encontrado para o filtro."
            >
              {filtrados.map((c) => (
                <LinhaSelecao
                  key={c.id}
                  inputId={`windx-conjunto-mobile-${c.id}`}
                  label={c.label}
                  detalhe={`${c.qtd_usinas} usina${c.qtd_usinas === 1 ? '' : 's'} · ${formatarMw(c.potencia_total_mw)}`}
                  selecionado={selecao.isSelecionado(c.id)}
                  onToggle={() => selecao.toggle(c.id)}
                />
              ))}
            </MobileListState>
          </div>

          <Rodape
            selecionadas={selecao.count}
            resumo={resumo}
            onLimpar={selecao.limpar}
            onCancelar={dismiss}
            onAplicar={handleAplicar}
          />

          <MobileFilterSheet
            open={filtrosAbertos}
            dismiss={() => setFiltrosAbertos(false)}
            title="Filtrar conjuntos"
            onClear={filtrosHook.limparFiltros}
            onApply={() => setFiltrosAbertos(false)}
          >
            <FiltroCategoria
              label="UF"
              opcoes={ufsDisponiveis}
              selecionadas={filtros.ufs}
              onChange={filtrosHook.setUfs}
              open={categoriaAberta === 'uf'}
              onToggle={toggleCategoria('uf')}
            />
            <FiltroCategoria
              label="Subsistema SM"
              opcoes={smDisponiveis}
              selecionadas={filtros.subsistemasSm}
              onChange={filtrosHook.setSubsistemasSm}
              open={categoriaAberta === 'sm'}
              onToggle={toggleCategoria('sm')}
            />
            <FiltroCategoria
              label="Subsistema SH"
              opcoes={shDisponiveis}
              selecionadas={filtros.subsistemasSh}
              onChange={filtrosHook.setSubsistemasSh}
              open={categoriaAberta === 'sh'}
              onToggle={toggleCategoria('sh')}
            />
            <FiltroCategoria
              label="Localização EPE"
              opcoes={epeDisponiveis}
              selecionadas={filtros.localizacoesEpe}
              onChange={filtrosHook.setLocalizacoesEpe}
              open={categoriaAberta === 'epe'}
              onToggle={toggleCategoria('epe')}
            />
          </MobileFilterSheet>
        </>
        )}
      </>
    </BottomSheet>
  );
}
